import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import {
  isTaxCalculationOutput,
  UserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'

import type { StateBreakdownData } from './state-breakdown/stateBreakdownData'

const monthLabel = (isoDate: string | null | undefined) =>
  isoDate
    ? isoToUTCDateTime(isoDate).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR_LONG)
    : null

/**
 * Sentences that explain an estimate the way a person would say it out loud,
 * for readers who won't work through a table of subtractions. Each sentence is
 * derived from the calculation, and any sentence whose inputs are missing is
 * left out rather than guessed at.
 */
export const federalSummarySentences = ({
  taxCalc,
  estimateDetails,
  displayInCents,
}: {
  taxCalc: UserTaxCalculation | undefined
  estimateDetails: QuarterlyTaxEstimateDetail | undefined
  displayInCents: number | null
}): string[] => {
  const output = taxCalc?.taxCalculationOutput
  if (!taxCalc || !isTaxCalculationOutput(output)) {
    return []
  }

  const sentences: string[] = []
  const quarter = estimateDetails?.taxQuarter
  const periodNetInCents =
    (taxCalc.periodIncomeInCents ?? 0) - (taxCalc.periodExpensesInCents ?? 0)
  const projectedNetInCents = taxCalc.annualizedNetIncomeInCents
  const through = monthLabel(estimateDetails?.bookkeepingPeriodEndsAt)

  // The calculator only scales year-to-date profit up to a full year when the
  // practice made money; a loss is used as-is, so the wording changes.
  if (periodNetInCents > 0 && through && projectedNetInCents) {
    sentences.push(
      `Your practice has made ${formatCurrencyFromCents(
        periodNetInCents
      )} through ${through}. We expect that to reach about ${formatCurrencyFromCents(
        projectedNetInCents
      )} by the end of the year, and we tax that full-year figure.`
    )
  } else if (periodNetInCents < 0) {
    sentences.push(
      `Your practice has spent more than it earned so far this year, so we used your actual year-to-date figure instead of projecting it forward.`
    )
  }

  const otherIncomeInCents =
    (output.data.annualGrossIncomeInCents ?? 0) - (projectedNetInCents ?? 0)
  if (otherIncomeInCents > 0) {
    sentences.push(
      `We add the other income in your household — jobs, contract work, and your spouse's income if you file together — which brings the total we tax to ${formatCurrencyFromCents(
        output.data.annualGrossIncomeInCents
      )}.`
    )
  }

  if (quarter) {
    sentences.push(
      `Taxes on that come to ${formatCurrencyFromCents(
        output.data.YTDGrossFederalTaxLiabilityInCents
      )} for everything owed through Q${quarter} — that's ${
        Number(quarter) * 25
      }% of the year, not this quarter alone.`
    )
  }

  const creditsInCents =
    (output.data.annualTotalFederalWithholdingInCents ?? 0) +
    (output.data.YTDFederalTaxPaymentsMadeInCents ?? 0)
  const federalPaymentsGated =
    output.data.allFederalQuarterPaymentsLogged === false && quarter !== '4'

  if (displayInCents !== null) {
    if (creditsInCents > 0) {
      sentences.push(
        `After the ${formatCurrencyFromCents(
          creditsInCents
        )} already covered by paycheck withholding and payments you've made, ${formatCurrencyFromCents(
          displayInCents
        )} is left to pay.`
      )
    } else if (!federalPaymentsGated) {
      sentences.push(
        `Nothing has been withheld or paid toward this year yet, so the full ${formatCurrencyFromCents(
          displayInCents
        )} is due.`
      )
    }
  }

  return sentences
}

export const stateSummarySentences = ({
  data,
  displayInCents,
}: {
  data: StateBreakdownData
  displayInCents: number | null
}): string[] => {
  if (data.isNoIncomeTaxState) {
    return [
      `${data.state} doesn't tax income like yours, so there's nothing to pay there.`,
    ]
  }

  const sentences: string[] = []

  if (data.isHomeState) {
    sentences.push(
      `${data.state} is where you live, so it counts everything your household earns${
        data.incomeInCents === null
          ? ''
          : ` — ${formatCurrencyFromCents(data.incomeInCents)}`
      } — not just the part you earned there.`
    )
  } else if (data.apportionment) {
    sentences.push(
      `You earned ${data.apportionment.percentage}% of your practice income in ${
        data.state
      } — ${formatCurrencyFromCents(
        data.incomeInCents ?? 0
      )}. ${data.state} taxes that share, not your full household income.`
    )
  }

  if (data.stateStandardDeductionInCents === 0) {
    sentences.push(
      `Some states set aside part of your income as untaxed before working out the tax. ${
        data.state
      } doesn't, so the full ${formatCurrencyFromCents(
        data.taxableIncomeInCents ?? data.incomeInCents ?? 0
      )} is taxed.`
    )
  } else if (data.stateStandardDeductionInCents !== null) {
    sentences.push(
      `Before working out the tax, ${data.state} sets aside ${formatCurrencyFromCents(
        data.stateStandardDeductionInCents
      )} as untaxed, leaving ${formatCurrencyFromCents(
        data.taxableIncomeInCents ?? 0
      )}.`
    )
  }

  if (displayInCents !== null) {
    const creditsInCents =
      (data.creditedPaymentsInCents ?? 0) +
      (data.withholding?.annualTotalInCents ?? 0)
    if (creditsInCents > 0) {
      sentences.push(
        `After the ${formatCurrencyFromCents(
          creditsInCents
        )} already paid toward ${data.state}, ${formatCurrencyFromCents(
          displayInCents
        )} is left to pay this quarter.`
      )
    } else if (data.allQuarterPaymentsLogged !== false) {
      sentences.push(
        `Nothing has been paid toward ${data.state} yet this year, so ${formatCurrencyFromCents(
          displayInCents
        )} is due this quarter.`
      )
    }
  }

  return sentences
}
